import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-5.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-7.svg";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async"; // ✅ Import Helmet
import SearchBar from "./SearchBar";

const HeaderRow = tw.div`flex justify-center items-center flex-col w-full`;
const Header = styled(SectionHeading)`
  width: 100%;                /* Occupy full width to allow text-center to work */
  text-align: center !important;
  font-size: 30px !important;
  font-weight: 600;           /* 'semi-bold' is usually 600 in CSS */
  font-family: 'Urbanist', sans-serif !important;
  line-height: 1.2;
  align-self: center;         /* Changed from flex-start to center */
  margin-left: 0;
  margin-bottom: 20px;        /* Added some breathing room above the search bar */
`;

const TabContent = tw(motion.div)`mt-6 flex flex-wrap sm:-mr-10 md:-mr-6 lg:-mr-12`;
const CardContainer = tw.div`mt-10 w-full sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/4 sm:pr-6 lg:pr-8`;
const Card = tw(motion.a)`rounded-b block max-w-xs mx-auto sm:max-w-none sm:mx-0 mb-0`;
const CardImageContainer = styled.div`
  ${props => css`background-image: url("${props.imageSrc}");`}
  ${tw`h-56 xl:h-64 bg-center relative rounded-t transition-transform duration-300 ease-in-out group-hover:scale-105`}
  background-size: contain;
  background-repeat: no-repeat;
  background-color: #fff;
`;
const CardButton = tw(PrimaryButtonBase)`
  text-sm flex justify-center items-center mt-4 rounded-full px-10 py-2
`;
const CardButtonContainer = tw.div`
  flex justify-center items-center mt-2 w-auto
`;
const CardText = tw.div`text-center text-[#000000]`;
const CardTitle = tw.h5`text-sm font-bold text-center text-[#000000]`;
const CardPrice = tw.p`mt-2 font-semibold text-center text-[#4e7662] text-[14px]`;

const ProductTabs = ({ heading = "Nos_produits", products = [] }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const role = localStorage.getItem("role");

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  const handleGoToProduct = (card) => {
    const productName =
      card.Name?.[lang] ||
      card.Name?.fr ||
      card.Name?.ar ||
      "Produit";
    const slug = productName.replace(/\s+/g, "-").toLowerCase();
    navigate(`/product-details/${card.ProductID}-${slug}`);
  };

  return (
    <>
      {/* ✅ SEO Metadata Section */}
      <Helmet>
        <html lang={lang} />
        <title>Boutique Produits Naturels et Bio Rehab Bio </title>
        <meta name="description" content="À la recherche de Boutique Produits Naturels & Bio ? Optez pour une solution 100% naturelle et bio pour votre bien-être. Commandez dès maintenant et profitez de la livraison express partout au Maroc !" />
        <link rel="canonical" href="https://rehabbio.ma/boutique" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Boutique Produits Naturels et Bio Rehab Bio Maroc" />
        <meta property="og:description" content="À la recherche de Boutique Produits Naturels & Bio ? Optez pour une solution 100% naturelle et bio pour votre bien-être. Commandez dès maintenant et profitez de la livraison express partout au Maroc !" />
        <meta property="og:url" content="https://rehabbio.ma/boutique" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rehab Bio" />
      </Helmet>

      <Container style={{ marginTop: "-50px" }}>
        <ContentWithPaddingXl>
          <HeaderRow>
            <Header>Boutique Produits Naturels & Bio</Header>

            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              language={i18n.language}
              allProducts={products}
              onResults={setFilteredProducts}
            />
          </HeaderRow>

          <TabContent>
            {products.length === 0 ? (
              <p>Téléchargement en cours…</p>
            ) : filteredProducts.length === 0 ? (
              <p style={{ textAlign: "center", width: "100%", color: "#9ca3af", marginTop: "2rem" }}>
                {i18n.language === "ar" ? "لا توجد نتائج" : "Aucun produit trouvé"}
              </p>
            ) : (
              filteredProducts.slice(0, 100).map((card) => {
                const productName =
                  card.Name?.[lang] ||
                  card.Name?.fr ||
                  card.Name?.ar ||
                  "Produit";

                return (
                  <CardContainer key={card.ProductID}>
                    <Card className="group" initial="rest" whileHover="hover" animate="rest">
                      <div onClick={() => handleGoToProduct(card)} style={{ cursor: "pointer" }}>
                        <CardImageContainer imageSrc={`https://rehabwmg.online/storage/${card.image}`} />
                      </div>
                      <CardText>
                        <CardPrice>
                            {(role === "supplier" || role === "influenceur" )
                            ? `${card.distributeur_price} ${t("currency")}`
                            : `${card.user_price} ${t("currency")}`}
                        </CardPrice>
                        <CardTitle>{productName}</CardTitle>
                        <CardText>{card.slug?.[i18n.language] && card.slug[i18n.language]}</CardText>
                        <CardButtonContainer>
                          <CardButton onClick={() => handleGoToProduct(card)}>
                            <i className="bi bi-cart-plus"></i>{t('add_to_cart')}
                          </CardButton>
                        </CardButtonContainer>
                      </CardText>
                    </Card>
                  </CardContainer>
                );
              })
            )}
          </TabContent>
        </ContentWithPaddingXl>
      </Container>
    </>
  );
};

export default ProductTabs;